import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, CircularProgress } from '@mui/material';
import { Box } from '@mui/system';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';
import { PDFJobRibbonProps } from './PDFJobRibbon.props';
import { ApiService, RetrieveDocumentGenerationJob } from '../../../../../api';
import { useStore } from '../../../../../common/store';
import { useAsyncProcessor } from '../../../../../context/AsyncProcessorContext/AsyncProcessorContext';
import { Logger } from '../../../../../utilities';

export const PDFJobRibbon = ({ generationJob, measurementDate, referenceObjectType = 'ct' }: PDFJobRibbonProps) => {
  const { addJob, removeJob } = useAsyncProcessor();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { apiCompanyDocumentGenerationJobsRead, apiCompanyDocumentGenerationJobsDocumentExecutionSessionsList } =
    ApiService;
  const [{ companyInfo }] = useStore();
  const [genJob, setGenJob] = useState(generationJob);

  // Capture the location where the job was started
  const jobStartLocation = useRef({ pathname: location.pathname, search: location.search });

  const createFileReferencesFromJob = useCallback(async () => {
    try {
      if (!measurementDate?.id || !genJob.id || !companyInfo.id) {
        return;
      }

      // the page param is autogenerated from the lib apiCompanyDocumentGenerationJobsDocumentExecutionSessionsList
      // we dont need it so it is set to undefined
      const page = undefined;
      const createFileReferences = true;

      await apiCompanyDocumentGenerationJobsDocumentExecutionSessionsList(
        companyInfo.id,
        `${genJob.job_id}`,
        page,
        createFileReferences,
        measurementDate.id,
        referenceObjectType
      );
    } catch (error) {
      Logger.error('Error creating file references:', error);
      return [];
    }
  }, [
    apiCompanyDocumentGenerationJobsDocumentExecutionSessionsList,
    genJob,
    companyInfo,
    measurementDate?.id,
    referenceObjectType,
  ]);

  const handleResult = useCallback(
    async (result: RetrieveDocumentGenerationJob) => {
      const response = result;
      setGenJob(response);

      if (response.status !== RetrieveDocumentGenerationJob.status.COMPLETED) {
        return;
      }

      removeJob(generationJob.job_id);

      try {
        await createFileReferencesFromJob();

        // Show success snackbar with navigation button
        enqueueSnackbar('Processing job completed', {
          variant: 'success',
          action: snackbarId => (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                closeSnackbar(snackbarId);
                navigate(`${jobStartLocation.current.pathname}${jobStartLocation.current.search}`);
              }}>
              View
            </Button>
          ),
        });
      } catch (error) {
        Logger.error('Error creating file references:', error);
        enqueueSnackbar('Error creating file references from extraction results', { variant: 'error' });
      }
    },
    [removeJob, generationJob.job_id, createFileReferencesFromJob, enqueueSnackbar, closeSnackbar, navigate]
  );

  useEffect(() => {
    if (!generationJob.id) return;

    addJob({
      id: `${generationJob.job_id}`,
      details: {
        description: 'AI Assisted PDF Processing',
        title: 'PDF Processing',
      },
      pollingConfig: {
        pollingInterval: 3000,
        pollingArgs: [companyInfo.id, generationJob.job_id],
        pollingFunction: apiCompanyDocumentGenerationJobsRead,
      },
      onError: err => {
        Logger.error('Error on parsing', err);
        enqueueSnackbar('Processing job completed', { variant: 'error' });
      },
      onResult: handleResult,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationJob]);

  const renderJobStatusAlert = () => {
    const jobStatus = genJob.status;
    if (
      jobStatus === RetrieveDocumentGenerationJob.status.PENDING ||
      jobStatus === RetrieveDocumentGenerationJob.status.IN_PROGRESS
    ) {
      return (
        <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ display: 'flex', alignItems: 'center' }}>
          Processing document, You are free to navigate to other pages. Upon completion, you will be notified! Current
          Status: {jobStatus}
        </Alert>
      );
    }

    if (jobStatus === RetrieveDocumentGenerationJob.status.COMPLETED) {
      return <Alert severity="success">Document processing completed successfully!</Alert>;
    }

    if (jobStatus === RetrieveDocumentGenerationJob.status.FAILED) {
      return <Alert severity="error">Document processing failed. Please try again.</Alert>;
    }

    return null;
  };

  return <Box>{genJob && <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>{renderJobStatusAlert()}</Box>}</Box>;
};
